import axios from 'axios';
import {
  fetchLabelPreviewHtml,
  fetchReceiptPreviewHtml,
  printTicketLabel,
  printTicketReceipt,
  printInventoryLabels,
  previewInventoryLabels,
  type InventoryLabelPrintItem,
} from '@/lib/api/print.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

export function openPrintWindow(html: string, type: 'label' | 'receipt'): void {
  const dimensions = type === 'label' ? 'width=500,height=700' : 'width=500,height=800';
  const printWindow = window.open('', '_blank', dimensions);
  if (!printWindow) {
    toastError('Popup blocked — please allow popups for printing');
    return;
  }

  printWindow.document.write(html);
  printWindow.document.close();

  const triggerPrint = () => {
    printWindow.focus();
    printWindow.print();
    printWindow.onafterprint = () => printWindow.close();
  };

  if (printWindow.document.readyState === 'complete') {
    triggerPrint();
  } else {
    printWindow.onload = triggerPrint;
  }
}

export async function printLabelBrowser(ticketId: string): Promise<void> {
  try {
    const html = await fetchLabelPreviewHtml(ticketId);
    openPrintWindow(html, 'label');
  } catch (err: unknown) {
    const parsed = parseApiError(err);
    toastError(parsed.message || 'Failed to load label for printing', parsed.code);
  }
}

export async function printReceiptBrowser(ticketId: string): Promise<void> {
  try {
    const html = await fetchReceiptPreviewHtml(ticketId);
    openPrintWindow(html, 'receipt');
  } catch (err: unknown) {
    const parsed = parseApiError(err);
    toastError(parsed.message || 'Failed to load receipt for printing', parsed.code);
  }
}

export async function printLabelPrintNode(ticketId: string): Promise<void> {
  try {
    await printTicketLabel(ticketId);
    toastSuccess('Label sent to printer');
  } catch (err: unknown) {
    const code = parseApiError(err).code;
    if (code === 'LABEL_PRINTER_NOT_SET' || code === 'PRINTER_NOT_CONFIGURED') {
      toastError('Label printer not configured — use browser print');
    } else {
      toastError(parseApiError(err).message, code);
    }
  }
}

export async function printReceiptPrintNode(ticketId: string): Promise<void> {
  try {
    await printTicketReceipt(ticketId);
    toastSuccess('Receipt sent to printer');
  } catch (err: unknown) {
    const code = parseApiError(err).code;
    if (code === 'RECEIPT_PRINTER_NOT_SET' || code === 'PRINTER_NOT_CONFIGURED') {
      toastError('Receipt printer not configured — use browser print');
    } else {
      toastError(parseApiError(err).message, code);
    }
  }
}

/** After ticket create — PrintNode when configured, otherwise browser. */
export async function printLabelAfterCreate(ticketId: string, hasPrintNode: boolean): Promise<void> {
  if (hasPrintNode) {
    await printLabelPrintNode(ticketId);
  } else {
    await printLabelBrowser(ticketId);
  }
}

function htmlFromPrintError(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null;
  const body = err.response?.data as
    | { data?: { html?: string; browserFallback?: boolean } }
    | undefined;
  if (body?.data?.html) return body.data.html;
  return null;
}

/**
 * Print inventory shelf labels. Tries PrintNode; falls back to browser print
 * when printer is unset / offline (API returns HTML in the error payload).
 */
export async function printInventoryLabelsWithFallback(
  items: InventoryLabelPrintItem[],
  opts?: { preferBrowser?: boolean },
): Promise<{ mode: 'printnode' | 'browser'; warnings: string[] }> {
  if (items.length === 0) {
    toastError('No items to label');
    return { mode: 'browser', warnings: [] };
  }

  if (opts?.preferBrowser) {
    try {
      const res = await printInventoryLabels({ items, htmlOnly: true });
      if (res.html) {
        openPrintWindow(res.html, 'label');
        toastSuccess(`Opened ${res.labelCount ?? items.length} label(s) for browser print`);
        return { mode: 'browser', warnings: res.warnings ?? [] };
      }
    } catch (err) {
      toastError(parseApiError(err).message);
    }
    return { mode: 'browser', warnings: [] };
  }

  try {
    const res = await printInventoryLabels({ items });
    if (res.warnings?.length) {
      toastSuccess(`Sent ${res.labelCount ?? items.length} label(s) — ${res.warnings[0]}`);
    } else {
      toastSuccess(`Sent ${res.labelCount ?? items.length} label(s) to printer`);
    }
    return { mode: 'printnode', warnings: res.warnings ?? [] };
  } catch (err) {
    const html = htmlFromPrintError(err);
    if (html) {
      openPrintWindow(html, 'label');
      toastSuccess('Printer unavailable — opened browser print');
      return { mode: 'browser', warnings: [] };
    }
    toastError(parseApiError(err).message);
    return { mode: 'browser', warnings: [] };
  }
}

export { previewInventoryLabels };
